import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  ListItemText,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { Checkbox, FormControlLabel } from "@mui/material";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const downloadBlob = (data, contentType, fileName) => {
  const blob = new Blob([data], { type: contentType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const multiSelectSx = {
  minWidth: 180,
  "& .MuiOutlinedInput-root": {
    minHeight: "40px",
    fontSize: "12px",
  },
  "& .MuiSelect-select": {
    fontSize: "12px",
    paddingTop: "9px",
    paddingBottom: "9px",
  },
  "& .MuiInputLabel-root, & .MuiInputLabel-shrink": {
    fontSize: "12px",
  },
};

const menuProps = {
  PaperProps: {
    sx: {
      maxHeight: 320,
      "& .MuiMenuItem-root": {
        fontSize: "13px",
        minHeight: "36px",
      },
      "& .MuiCheckbox-root": {
        padding: "4px",
      },
      "& .MuiListItemText-primary": {
        fontSize: "13px",
      },
    },
  },
};

export default function DfcReport() {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [newPagePerBranch, setNewPagePerBranch] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [branches, setBranches] = useState([]);

  // Empty arrays mean All Programs / All Branches.
  const [programIds, setProgramIds] = useState([]);
  const [branchIds, setBranchIds] = useState([]);

  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loading, setLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${sessionStorage.getItem("token")}`,
  });

  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  const clientId = user?.client_id;
  const collegeId = user?.college_id;

  const programNameById = useMemo(
    () => new Map(programs.map((p) => [Number(p.program_id), p.program_name])),
    [programs]
  );

  const branchNameById = useMemo(
    () => new Map(branches.map((b) => [Number(b.branch_id), b.branch_name])),
    [branches]
  );

  // Load programs.
  useEffect(() => {
    if (!clientId || !collegeId) return;

    let cancelled = false;

    const loadPrograms = async () => {
      setLoadingPrograms(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/api/programs/${clientId}/${collegeId}`, {
          headers: getAuthHeaders(),
        });

        if (!cancelled) {
          setPrograms(response.data || []);
        }
      } catch {
        if (!cancelled) {
          setPrograms([]);
          showSnackbar("Unable to load programs.", "error");
        }
      } finally {
        if (!cancelled) setLoadingPrograms(false);
      }
    };

    loadPrograms();

    return () => {
      cancelled = true;
    };
  }, [clientId, collegeId]);

  // Load branches belonging to the selected programs.
  // Existing branches API accepts one programId at a time.
  const programIdsKey = JSON.stringify(programIds);

  useEffect(() => {
    if (!clientId || !collegeId) return;

    let cancelled = false;

    const loadBranches = async () => {
      setLoadingBranches(true);

      try {
        let combined = [];

        if (programIds.length === 0) {
          const response = await axios.get(
            `${API_BASE_URL}/api/branches/${clientId}/${collegeId}`,
            { headers: getAuthHeaders() }
          );

          combined = response.data?.data || [];
        } else {
          const responses = await Promise.all(
            programIds.map((programId) =>
              axios.get(`${API_BASE_URL}/api/branches/${clientId}/${collegeId}`, {
                headers: getAuthHeaders(),
                params: { programId },
              })
            )
          );

          combined = responses.flatMap((response) => response.data?.data || []);
        }

        // A branch must appear only once even if returned more than once.
        const uniqueBranches = Array.from(
          new Map(combined.map((branch) => [Number(branch.branch_id), branch])).values()
        ).sort((a, b) => String(a.branch_name).localeCompare(String(b.branch_name)));

        if (!cancelled) {
          setBranches(uniqueBranches);
          const validIds = new Set(uniqueBranches.map((b) => Number(b.branch_id)));

          setBranchIds((previous) => previous.filter((id) => validIds.has(Number(id))));
        }
      } catch {
        if (!cancelled) {
          setBranches([]);
          setBranchIds([]);
          showSnackbar("Unable to load branches.", "error");
        }
      } finally {
        if (!cancelled) setLoadingBranches(false);
      }
    };

    loadBranches();

    return () => {
      cancelled = true;
    };
  }, [clientId, collegeId, programIdsKey]);

  const handleProgramChange = (event) => {
    const value = event.target.value;

    if (value === "ALL" || (Array.isArray(value) && value.includes("ALL"))) {
      setProgramIds([]);
      setBranchIds([]);
      return;
    }

    const selected = (typeof value === "string" ? value.split(",") : value)
      .map(Number)
      .filter(Number.isFinite);

    setProgramIds([...new Set(selected)]);
    // Branch selection is refreshed for the newly selected programs.
    setBranchIds([]);
  };

  const handleBranchChange = (event) => {
    const value = event.target.value;

    if (value === "ALL" || (Array.isArray(value) && value.includes("ALL"))) {
      setBranchIds([]);
      return;
    }

    const selected = (typeof value === "string" ? value.split(",") : value)
      .map(Number)
      .filter(Number.isFinite);

    setBranchIds([...new Set(selected)]);
  };

  const handleGetDfcReport = async () => {
    if (!clientId || !collegeId) {
      showSnackbar("User college details not found.", "error");
      return;
    }

    if (fromDate && toDate && fromDate > toDate) {
      showSnackbar("From Date cannot be after To Date.", "error");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        client_id: clientId,
        college_id: collegeId,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        payment_mode: paymentMode || undefined,
        program_ids: programIds,
        branch_ids: branchIds,
        newPagePerBranch,
      };

      const response = await axios.post(`${API_BASE_URL}/api/dfc-report/download-pdfs`, payload, {
        headers: getAuthHeaders(),
        responseType: "blob",
      });

      // One selected branch: retain the existing single-PDF behavior.
      if (branchIds.length === 1) {
        const branchName = branchNameById.get(Number(branchIds[0])) || "Branch";

        downloadBlob(response.data, "application/pdf", `${branchName} DFC REPORT.pdf`);

        showSnackbar("DFC report PDF downloaded.");
      } else {
        // Multiple branches, or All Branches:
        // ZIP contains individual branch PDFs and a consolidated PDF.
        downloadBlob(response.data, "application/zip", "DFC REPORTS.zip");

        showSnackbar("Branch-wise PDFs and consolidated DFC report downloaded.");
      }
    } catch (err) {
      let message = "Unable to download DFC report.";

      // With responseType=blob, backend JSON errors may also be blobs.
      if (err.response?.data instanceof Blob) {
        try {
          const errorData = JSON.parse(await err.response.data.text());
          message = errorData.message || message;
        } catch {
          // Keep the default message if the error is not JSON.
        }
      } else {
        message = err.response?.data?.message || message;
      }

      showSnackbar(message, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Card sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          DFC - DAILY FEE COLLECTION
        </Typography>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              select
              label="Program"
              value={programIds}
              onChange={handleProgramChange}
              size="medium"
              fullWidth
              disabled={loadingPrograms}
              InputLabelProps={{ shrink: true }}
              sx={multiSelectSx}
              SelectProps={{
                multiple: true,
                displayEmpty: true,
                renderValue: (selected) => {
                  if (!selected.length) return "All Programs";

                  const first = programNameById.get(Number(selected[0])) || selected[0];

                  return selected.length === 1 ? first : `${first} +${selected.length - 1}`;
                },
                MenuProps: menuProps,
              }}
            >
              <MenuItem value="ALL">
                <Checkbox checked={programIds.length === 0} />
                <ListItemText primary="All Programs" />
              </MenuItem>

              {programs.map((program) => (
                <MenuItem key={program.program_id} value={Number(program.program_id)}>
                  <Checkbox checked={programIds.includes(Number(program.program_id))} />
                  <ListItemText primary={program.program_name} />
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              select
              label="Branch"
              value={branchIds}
              onChange={handleBranchChange}
              size="medium"
              fullWidth
              disabled={loadingBranches}
              InputLabelProps={{ shrink: true }}
              sx={multiSelectSx}
              SelectProps={{
                multiple: true,
                displayEmpty: true,
                renderValue: (selected) => {
                  if (!selected.length) return "All Branches";

                  const first = branchNameById.get(Number(selected[0])) || selected[0];

                  return selected.length === 1 ? first : `${first} +${selected.length - 1}`;
                },
                MenuProps: menuProps,
              }}
            >
              <MenuItem value="ALL">
                <Checkbox checked={branchIds.length === 0} />
                <ListItemText primary="All Branches" />
              </MenuItem>

              {branches.map((branch) => (
                <MenuItem key={branch.branch_id} value={Number(branch.branch_id)}>
                  <Checkbox checked={branchIds.includes(Number(branch.branch_id))} />
                  <ListItemText primary={branch.branch_name} />
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={4} md={1.8}>
            <TextField
              label="From Date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="medium"
            />
          </Grid>

          <Grid item xs={12} sm={4} md={1.8}>
            <TextField
              label="To Date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="medium"
            />
          </Grid>

          <Grid item xs={12} sm={4} md={1.8}>
            <TextField
              select
              label="Payment Mode"
              value={paymentMode}
              onChange={(e) => setPaymentMode(e.target.value)}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true, displayEmpty: true }}
              fullWidth
              size="medium"
            >
              <option value="">All</option>
              <option value="CASH">CASH</option>
              <option value="ONLINE">ONLINE</option>
            </TextField>
          </Grid>

          <Grid item xs={12} md={2.4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={newPagePerBranch}
                  onChange={(event) => setNewPagePerBranch(event.target.checked)}
                  color="primary"
                />
              }
              label="New Page per Branch"
            />
            <Button
              variant="contained"
              onClick={handleGetDfcReport}
              disabled={loading || loadingBranches || loadingPrograms}
              sx={{ color: "#fff", minHeight: 40, width: "100%" }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : "Get DFC Report"}
            </Button>
          </Grid>
        </Grid>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
